import { useEffect } from 'react';
import {
	CONFLUENCE_FLAG_TO_SHARE_ACTION_EVENT,
	type ConfluenceFlagToShareActionDetail,
	type ConfluenceFlagToShareContentType,
} from '@post-office/growth-common/confluence-flag-to-share-events';

export type FlagToShareContentType = 'page' | 'whiteboard' | 'database';

const CONTENT_ROUTE_SEGMENT: Record<FlagToShareContentType, string> = {
	page: 'pages',
	whiteboard: 'whiteboard',
	database: 'database',
};

export const getLongContentLink = ({
	contentId,
	contentType,
	origin,
	pathname,
}: {
	contentId: string;
	contentType: FlagToShareContentType;
	origin: string;
	pathname: string;
}): string | undefined => {
	const routeSegment = CONTENT_ROUTE_SEGMENT[contentType];
	const spacePath = pathname.match(/^(.*\/spaces\/[^/]+)(?:\/|$)/)?.[1];
	if (!contentId || !spacePath) {
		return undefined;
	}

	return `${origin}${spacePath}/${routeSegment}/${encodeURIComponent(contentId)}`;
};

type ClipboardWriter = {
	writeText: (value: string) => Promise<void>;
};

export const copyToClipboard = (
	value: string | undefined,
	clipboard: ClipboardWriter | undefined,
): void => {
	if (!value || !clipboard || typeof clipboard.writeText !== 'function') {
		return;
	}
	void clipboard.writeText(value).catch(() => undefined);
};

const matchesContentType = (
	contentType: FlagToShareContentType,
	eventContentType: ConfluenceFlagToShareContentType,
): boolean =>
	eventContentType === 'live-doc' ? contentType === 'page' : eventContentType === contentType;

const isFlagToShareActionEvent = (
	event: Event,
): event is CustomEvent<ConfluenceFlagToShareActionDetail> => {
	if (!(event instanceof CustomEvent)) {
		return false;
	}
	const { action, contentId, contentType } = event.detail ?? {};
	return (
		(action === 'openShareDialog' || action === 'copyShareLink') &&
		typeof contentId === 'string' &&
		contentId.length > 0 &&
		(contentType === 'live-doc' || contentType === 'whiteboard' || contentType === 'database')
	);
};

export type UseFlagToShareActionsProps = {
	contentId: string;
	contentType: FlagToShareContentType;
	isOpenShareDialogDisabled: boolean;
	isCopyShareLinkDisabled: boolean;
	openShareDialog: () => void;
};

export const useFlagToShareActions = ({
	contentId,
	contentType,
	isOpenShareDialogDisabled,
	isCopyShareLinkDisabled,
	openShareDialog,
}: UseFlagToShareActionsProps): void => {
	useEffect(() => {
		const handleAction = (event: Event) => {
			if (
				event.defaultPrevented ||
				!isFlagToShareActionEvent(event) ||
				event.detail.contentId !== contentId ||
				!matchesContentType(contentType, event.detail.contentType)
			) {
				return;
			}

			if (event.detail.action === 'openShareDialog') {
				if (isOpenShareDialogDisabled) {
					return;
				}
				event.preventDefault();
				openShareDialog();
				return;
			}

			if (isCopyShareLinkDisabled) {
				return;
			}
			const longContentLink = getLongContentLink({
				contentId,
				contentType,
				origin: window.location.origin,
				pathname: window.location.pathname,
			});
			const clipboard = navigator?.clipboard;
			event.preventDefault();
			copyToClipboard(longContentLink, clipboard);
		};

		window.addEventListener(CONFLUENCE_FLAG_TO_SHARE_ACTION_EVENT, handleAction);
		return () => window.removeEventListener(CONFLUENCE_FLAG_TO_SHARE_ACTION_EVENT, handleAction);
	}, [contentId, contentType, isCopyShareLinkDisabled, isOpenShareDialogDisabled, openShareDialog]);
};
